/* eslint-disable react/function-component-definition */
/* eslint-disable import/no-unresolved */
import { Button } from "@mui/material";
import { useFormik } from "formik";
import { FC, memo, useEffect, useState } from "react";
import { MedicalQuestionnaireAPIData } from "src/helpers/FormDetails/ProposalDetails/interfaces";
import {
  calcAgeFromToday,
  constructFormikProps,
  isMedicalResponseNo,
  resetFormikState
} from "src/helpers/FormDetails/ProposalDetails/resources";
import { setStepDetails } from "src/redux/Actions/common";
import store from "src/redux/store";
import { setProposerMedicalLifestylePostDetails } from "src/redux/Thunk";
import ConfirmationModal from "src/shared/ConfirmationModal";
import CustomBackDrop from "src/shared/CustomBackDrop";
import CustomModal from "src/shared/CustomModal";
import { useAppSelector } from "src/utils/hooks/useAppSelector";
import UWQuestionnaire from "../../../../shared/UWQuestionnaire";

interface Props {}

const MedicalAndLifeStyleV2: FC<Props> = () => {
  const questionnaire: MedicalQuestionnaireAPIData.QuestionnaireReducer = useAppSelector(
    (state) => state?.masterdata_reducer?.questionnaire
  );
  const gender = useAppSelector((s) => s?.proposerDetails?.general?.genderCode) || "";
  const proposerStepNo = useAppSelector((state) => state.stepDetails?.proposerStepNo);
  const parentStepNo = useAppSelector((state) => state.stepDetails?.parentStepNo);
  const proposalStatus = useAppSelector((state) => state.proposalStatus.status);
  const [formikProperties, setFormikProperties] = useState<Record<string, any>>({
    initialValues: {},
    validationSchema: null
  });
  const nationality = useAppSelector((state) => state.proposerDetails?.general?.nationalityCode);
  const [errorJSON, setErrorJSON] = useState<Record<string, any>>({});
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [submissionLoading, setSubmissionLoading] = useState(false);
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
  const medicalLifeStyleV2Data = useAppSelector(
    (state) => state?.medicalAndLifeStyle?.medicalDetailsV2
  );
  const sumAssured = useAppSelector((state) => state?.common?.leadDetails?.sumAssured);
  const primaryLifeDob = useAppSelector((state) => state?.proposerDetails?.general?.dob) || "";
  const age = calcAgeFromToday(primaryLifeDob);

  const formik = useFormik({
    enableReinitialize: true,
    validateOnMount: true,
    validateOnChange: true,
    validateOnBlur: true,
    initialValues: formikProperties?.initialValues || {},
    ...(formikProperties?.validationSchema && {
      validationSchema: formikProperties?.validationSchema
    }),
    onSubmit: (fieldValues) => {
      if (proposalStatus !== "MEF Submitted") {
        if (Object.values(errorJSON)?.some(Boolean)) {
          setModalOpen(true);
        } else {
          setSubmissionLoading(true);
          const nextStepDetails =
            nationality !== "RES"
              ? { proposerStepNo: proposerStepNo + 1 }
              : { parentStepNo: parentStepNo + 1, jointStepNo: 1 };
          store.dispatch(setProposerMedicalLifestylePostDetails(fieldValues, nextStepDetails));
          window.scrollTo(0, 0);
          setSubmissionLoading(false);
        }
      }
    }
  });

  const handleSubmit = () => {
    if (!formik.isValid) {
      setModalOpen(true);
      formik.handleSubmit();
    } else if (isMedicalResponseNo(formik?.values)) {
      setOpenConfirmationModal(true);
    } else {
      formik.handleSubmit();
    }
  };

  useEffect(() => {
    const { initialValues, validationSchema } = constructFormikProps(
      questionnaire,
      { gender, sumAssured, age },
      medicalLifeStyleV2Data!
    );
    formik.setValues(initialValues);
    setFormikProperties({ initialValues, validationSchema });
  }, [medicalLifeStyleV2Data, questionnaire]);

  useEffect(() => {
    if (Object.keys(formikProperties?.initialValues)?.length) {
      resetFormikState(questionnaire, formik);
    }
  }, [formik?.values, formikProperties?.initialValues]);

  return (
    <>
      <CustomBackDrop
        open={submissionLoading || !Object.keys(formikProperties?.initialValues)?.length}
      />
      {openConfirmationModal && (
        <ConfirmationModal
          text={
            <div>
              <p>
                Dear Customer, you have answered all health questions as “NO”, if you want to make
                any changes to any of the answers, please resubmit the response.
              </p>
              <p className="text-sm mt-3">If not, then please proceed further.</p>
            </div>
          }
          onNext={() => formik.handleSubmit()}
          onCancel={() => setOpenConfirmationModal(false)}
        />
      )}
      <CustomModal modalOpen={modalOpen} setModalOpen={setModalOpen} />
      <div className="container mx-auto bg-white text-sm font-montserrat normal font-medium leading-50 space-y-5">
        <UWQuestionnaire
          formik={formik}
          errorJSON={errorJSON}
          setErrorJSON={setErrorJSON}
          editable
        />
        <div className="mt-10 flex justify-center">
          <Button
            variant="outlined"
            classes={{ outlined: "!rounded-lg" }}
            onClick={() => {
              store.dispatch(setStepDetails({ proposerStepNo: proposerStepNo - 1 }));
              window.scrollTo(0, 0);
            }}
            className="!text-black !uppercase h-13 w-37.5 rounded-xl !mr-10"
          >
            Back
          </Button>
          <Button
            variant="contained"
            classes={{ contained: "!rounded-lg" }}
            onClick={handleSubmit}
            className="!uppercase h-13 w-37.5"
          >
            NEXT
          </Button>
        </div>
      </div>
    </>
  );
};

export default memo(MedicalAndLifeStyleV2);
